import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useBrand } from "@/context/BrandContext";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n/I18nContext";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { NotificationBell } from "@/components/NotificationBell";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Menu, Search, User, LogOut, LayoutDashboard, MessageSquare, PlusCircle, Megaphone } from "lucide-react";

const Logo = ({ brand }) => (
  <Link to="/" data-testid="brand-logo-link" className="flex items-center gap-2.5">
    <span className="h-10 w-10 rounded-full bg-white ring-1 ring-border overflow-hidden flex items-center justify-center shadow-sm">
      <img src={brand.logoUrl} alt={brand.name} className="h-9 w-9 object-contain" />
    </span>
    <span className="flex flex-col leading-none">
      <span className="font-heading font-extrabold text-lg tracking-tight text-secondary">{brand.name}</span>
      <span className="text-[11px] text-muted-foreground hidden sm:block">{brand.region} · {brand.currency}</span>
    </span>
  </Link>
);

export const Header = () => {
  const brand = useBrand();
  const { user, logout } = useAuth();
  const { t } = useT();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);

  const nav = [
    { label: t("nav.home"), to: "/" },
    { label: t("nav.catalogue"), to: "/catalogue" },
  ];

  const dashboardPath =
    user?.role === "admin" ? "/admin" : user?.role === "seller" ? "/vendeur" : "/acheteur";

  const submit = (e) => {
    e.preventDefault();
    navigate(`/catalogue${q ? `?q=${encodeURIComponent(q)}` : ""}`);
    setOpen(false);
  };

  return (
    <header className="sticky top-0 z-50 bg-card/95 backdrop-blur border-b border-border">
      <div className="container-page flex items-center gap-3 h-16">
        <Logo brand={brand} />

        <nav className="hidden lg:flex items-center gap-1 ml-4">
          {nav.map((item) => (
            <Link key={item.to} to={item.to}
              data-testid={`nav-${item.to.replace("/", "") || "home"}`}
              className="px-3 py-2 rounded-md text-sm font-medium text-foreground/80 hover:text-primary hover:bg-muted transition-colors">
              {item.label}
            </Link>
          ))}
          {(user?.role === "buyer" || user?.role === "seller") && (
            <Link to="/offres" data-testid="nav-offers"
              className="px-3 py-2 rounded-md text-sm font-medium text-foreground/80 hover:text-primary hover:bg-muted transition-colors">
              {t("nav.offers")}
            </Link>
          )}
          {user?.role === "seller" && (
            <Link to="/vendeur" data-testid="nav-sell"
              className="px-3 py-2 rounded-md text-sm font-semibold text-primary hover:bg-muted transition-colors">
              {t("nav.sell")}
            </Link>
          )}
        </nav>

        <form onSubmit={submit} className="hidden md:flex flex-1 max-w-xs ml-auto">
          <div className="relative w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            <input data-testid="header-search-input" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder={t("hero.searchPlaceholder")}
              className="w-full h-10 pl-9 pr-3 rounded-full bg-muted border border-transparent focus:border-primary focus:bg-card outline-none text-sm transition-colors" />
          </div>
        </form>

        <div className="hidden md:block ml-auto md:ml-2"><LanguageSwitcher /></div>

        {user ? (
          <>
            <NotificationBell />
            <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="rounded-full gap-2 hidden sm:inline-flex" data-testid="user-menu-btn">
                <User className="h-4 w-4" /> <span className="max-w-[120px] truncate">{user.name}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem onClick={() => navigate("/profil")} data-testid="menu-profile">
                <User className="h-4 w-4 mr-2" /> {t("nav.profile")}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate(dashboardPath)} data-testid="menu-dashboard">
                <LayoutDashboard className="h-4 w-4 mr-2" /> {user.role === "admin" ? t("nav.admin") : t("nav.dashboard")}
              </DropdownMenuItem>
              {user.role === "seller" && (
                <DropdownMenuItem onClick={() => navigate("/vendeur")} data-testid="menu-sell">
                  <PlusCircle className="h-4 w-4 mr-2" /> {t("nav.myListings")}
                </DropdownMenuItem>
              )}
              {user.role !== "admin" && (
                <DropdownMenuItem onClick={() => navigate("/offres")} data-testid="menu-offers">
                  <Megaphone className="h-4 w-4 mr-2" /> {t("nav.offers")}
                </DropdownMenuItem>
              )}
              {user.role !== "admin" && (
                <DropdownMenuItem onClick={() => navigate("/echanges")} data-testid="menu-messages">
                  <MessageSquare className="h-4 w-4 mr-2" /> {t("nav.messages")}
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => { logout(); navigate("/"); }} data-testid="menu-logout">
                <LogOut className="h-4 w-4 mr-2" /> {t("nav.logout")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          </>
        ) : (
          <div className="hidden sm:flex items-center gap-2">
            <Button variant="ghost" onClick={() => navigate("/connexion")} data-testid="login-btn" className="rounded-full">
              {t("nav.login")}
            </Button>
            <Button onClick={() => navigate("/inscription")} data-testid="register-btn"
              className="rounded-full bg-secondary hover:bg-secondary/90 text-secondary-foreground">
              {t("nav.register")}
            </Button>
          </div>
        )}

        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" className="lg:hidden" data-testid="mobile-menu-btn">
              <Menu className="h-5 w-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="right" className="w-80 overflow-y-auto">
            <div className="mt-8 flex flex-col gap-1">
              <form onSubmit={submit} className="mb-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                  <input data-testid="mobile-search-input" value={q} onChange={(e) => setQ(e.target.value)}
                    placeholder={t("common.search")}
                    className="w-full h-11 pl-9 pr-3 rounded-full bg-muted outline-none text-sm" />
                </div>
              </form>
              <div className="mb-2"><LanguageSwitcher /></div>
              {nav.map((item) => (
                <Link key={item.to} to={item.to} onClick={() => setOpen(false)}
                  className="px-3 py-3 rounded-lg text-base font-medium hover:bg-muted transition-colors">
                  {item.label}
                </Link>
              ))}
              {user ? (
                <>
                  <Link to="/profil" onClick={() => setOpen(false)} data-testid="mobile-profile"
                    className="px-3 py-3 rounded-lg text-base font-medium hover:bg-muted">
                    {t("nav.profile")}
                  </Link>
                  <Link to={dashboardPath} onClick={() => setOpen(false)} data-testid="mobile-dashboard"
                    className="px-3 py-3 rounded-lg text-base font-medium hover:bg-muted">
                    {user.role === "admin" ? t("nav.admin") : t("nav.dashboard")}
                  </Link>
                  {user.role !== "admin" && (
                    <Link to="/offres" onClick={() => setOpen(false)} data-testid="mobile-offers"
                      className="px-3 py-3 rounded-lg text-base font-medium hover:bg-muted">{t("nav.offers")}</Link>
                  )}
                  {user.role !== "admin" && (
                    <Link to="/echanges" onClick={() => setOpen(false)}
                      className="px-3 py-3 rounded-lg text-base font-medium hover:bg-muted">{t("nav.messages")}</Link>
                  )}
                  <button onClick={() => { logout(); setOpen(false); navigate("/"); }}
                    className="text-left px-3 py-3 rounded-lg text-base font-medium hover:bg-muted text-destructive">
                    {t("nav.logout")}
                  </button>
                </>
              ) : (
                <>
                  <Link to="/connexion" onClick={() => setOpen(false)} className="px-3 py-3 rounded-lg text-base font-medium hover:bg-muted">{t("nav.login")}</Link>
                  <Link to="/inscription" onClick={() => setOpen(false)} className="px-3 py-3 rounded-lg text-base font-semibold text-primary hover:bg-muted">{t("nav.register")}</Link>
                </>
              )}
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
};
